import { BaseController } from 'controllers/BaseController'
import { ProjectsAPI } from 'controllers/Projects/ProjectsAPI'
import {
  fetchCurrentProject,
  fetchProject,
} from 'features/BillOfMaterials/store/asyncActions/fetchProject'
import { newProjectActions } from 'features/BillOfMaterials/store/projectReducer'
import { isEqual } from 'lodash'
import {
  PriceSummaryDto,
  QuantityDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { TelemetryService } from 'services/Telemetry'
import { store } from 'store/configureStore'
import { getPriceSummaryKey } from 'store/Project/ProjectStateUtils'
import { ProjectState } from 'store/Project/ProjectTypes'
import { RecursivePartial } from 'utils/RecursivePartial'
import { ProjectArticleAPI } from './ProjectArticleAPI'
import { ProjectResourceAPI } from './ProjectResourceAPI'

const notEqual = (a, b) => !isEqual(a, b)

class PriceSummariesController extends BaseController<ProjectsAPI> {
  resourceAPIs: Record<string, ProjectResourceAPI> = {}
  articleAPI: ProjectArticleAPI

  private getResourceAPI = (resourceId: string) => {
    if (!this.resourceAPIs[resourceId]) {
      this.resourceAPIs[resourceId] = new ProjectResourceAPI(
        this.partyId,
        this.projectId,
        resourceId,
        this.onRequestChange,
      )
    }

    return this.resourceAPIs[resourceId]
  }

  private getArticleAPI = () => {
    if (!this.articleAPI) {
      this.articleAPI = new ProjectArticleAPI(
        this.partyId,
        this.projectId,
        this.onRequestChange,
      )
    }

    return this.articleAPI
  }

  constructor(
    private partyId: string,
    private projectId: string,
  ) {
    super(() => {
      return null
    })
  }

  public SaveArticleIdSurchargeRatio = async (
    articleId: string,
    surchargeRatio: QuantityDto,
  ) => {
    try {
      await this.getArticleAPI().SetArticleSurchargeRatio(
        articleId,
        surchargeRatio,
      )
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public SaveArticleIdSurchargeValue = async (
    articleId: string,
    surchargeValue: QuantityDto,
  ) => {
    try {
      await this.getArticleAPI().SetArticleSurchargeValue(
        articleId,
        surchargeValue,
      )
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public SaveArticleIdDiscountRatio = async (
    articleId: string,
    discountRatio: QuantityDto,
  ) => {
    try {
      await this.getArticleAPI().SetArticleDiscountRatio(
        articleId,
        discountRatio,
      )
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async SavePriceSummary(
    changedData: Partial<PriceSummaryDto>,
    initialData: PriceSummaryDto,
  ) {
    try {
      store.dispatch(
        newProjectActions.setProjectStateProperties({
          priceSummaries: {
            [getPriceSummaryKey(changedData)]: {
              ...changedData,
              totalSalesPrice: { value: null },
              totalCostPrice: { value: null },
            },
          },
        }),
      )

      const apiCalls = this.getUpdatesCalls(changedData, initialData)
      await Promise.all(apiCalls.map((updateAction) => updateAction()))
    } catch (err) {
      TelemetryService.getInstance().logError(err)
      store.dispatch(fetchCurrentProject(null))
      throw err
    }
  }

  private getUpdatesCalls(
    changedData: Partial<PriceSummaryDto>,
    initialData: PriceSummaryDto,
  ) {
    const isMaterial = initialData?.isMaterial
    const isPurchasePart = initialData.isPurchasePart
    const isPurchasePartWithoutArticle =
      initialData?.isPurchasePartWithoutArticle
    const isWorkingStep = initialData?.isWorkingStep

    const updates = []

    if (isMaterial || isPurchasePart || isPurchasePartWithoutArticle) {
      if (notEqual(changedData.rate, initialData.rate)) {
        updates.push(() =>
          this.getArticleAPI().SetArticleCostPrice(
            initialData.materialId,
            changedData.rate,
          ),
        )
      }

      if (notEqual(changedData.surchargeRatio, initialData.surchargeRatio)) {
        updates.push(() =>
          this.getArticleAPI().SetArticleSurchargeRatio(
            initialData.materialId,
            changedData.surchargeRatio,
          ),
        )
      }
      if (notEqual(changedData.surchargeValue, initialData.surchargeValue)) {
        updates.push(() =>
          this.getArticleAPI().SetArticleSurchargeValue(
            initialData.materialId,
            changedData.surchargeValue,
          ),
        )
      }

      if (notEqual(changedData.discountRatio, initialData.discountRatio)) {
        updates.push(() =>
          this.getArticleAPI().SetArticleDiscountRatio(
            initialData.materialId,
            changedData.discountRatio,
          ),
        )
      }
      if (notEqual(changedData.discountValue, initialData.discountValue)) {
        updates.push(() =>
          this.getArticleAPI().SetArticleDiscountValue(
            initialData.materialId,
            changedData.discountValue,
          ),
        )
      }
    }

    if (isWorkingStep) {
      if (notEqual(changedData.rate, initialData.rate)) {
        updates.push(() =>
          this.getResourceAPI(
            initialData.workingStep.resource.id,
          ).SetResourceWorkingStepCostPrice(
            changedData.rate,
            initialData.workingStep.secondaryWorkingStep,
          ),
        )
      }

      if (notEqual(changedData.surchargeRatio, initialData.surchargeRatio)) {
        updates.push(() =>
          this.getResourceAPI(
            initialData.workingStep.resource.id,
          ).SetResourceSurcharge({
            surchargeRatio: changedData.surchargeRatio,
          }),
        )
      }
      if (notEqual(changedData.surchargeValue, initialData.surchargeValue)) {
        updates.push(() =>
          this.getResourceAPI(
            initialData.workingStep.resource.id,
          ).SetResourceSurcharge({
            surchargeValue: changedData.surchargeValue,
          }),
        )
      }

      if (notEqual(changedData.discountRatio, initialData.discountRatio)) {
        updates.push(() =>
          this.getResourceAPI(
            initialData.workingStep.resource.id,
          ).SetResourceDiscount({
            discountRatio: changedData.discountRatio,
          }),
        )
      }
      if (notEqual(changedData.discountValue, initialData.discountValue)) {
        updates.push(() =>
          this.getResourceAPI(
            initialData.workingStep.resource.id,
          ).SetResourceDiscount({
            discountValue: changedData.discountValue,
          }),
        )
      }
    }
    return updates
  }

  public async ResetToDefault(
    valueType: 'costPrice' | 'discount' | 'surcharge',
    data: PriceSummaryDto,
  ) {
    try {
      const apiCalls = this.getResetToDefaultCalls(valueType, data)

      store.dispatch(
        newProjectActions.setProjectStateProperties({
          priceSummaries: {
            [getPriceSummaryKey(data)]: {
              [valueType === 'surcharge' ? 'surchargeValue' : 'discountValue']:
                {
                  value: null,
                },
              totalSalesPrice: { value: null },
            },
          },
        } as RecursivePartial<ProjectState>),
      )

      await Promise.all(apiCalls.map((updateAction) => updateAction()))
    } catch (err) {
      TelemetryService.getInstance().logError(err)
      store.dispatch(fetchProject({ projectId: this.projectId }))
      throw err
    }
  }

  private getResetToDefaultCalls(
    valueType: 'costPrice' | 'discount' | 'surcharge',
    data: PriceSummaryDto,
  ) {
    const isMaterial = data?.isMaterial
    const isPurchasePart = data.isPurchasePart
    const isWorkingStep = data?.isWorkingStep

    const updates = []

    if (isMaterial || isPurchasePart) {
      if (valueType === 'costPrice') {
        updates.push(() =>
          this.getArticleAPI().DeleteArticleCostPrice(data.materialId),
        )
      }
      if (valueType === 'discount') {
        updates.push(() =>
          this.getArticleAPI().DeleteArticleDiscount(data.materialId),
        )
      }
      if (valueType === 'surcharge') {
        updates.push(() =>
          this.getArticleAPI().DeleteArticleSurcharge(data.materialId),
        )
      }
    }

    if (isWorkingStep) {
      if (valueType === 'costPrice') {
        updates.push(() =>
          this.getResourceAPI(
            data.workingStep.resource.id,
          ).DeleteResourceWorkingStepCostPrice(
            data.workingStep.secondaryWorkingStep,
          ),
        )
      }
      if (valueType === 'discount') {
        updates.push(() =>
          this.getResourceAPI(
            data.workingStep.resource.id,
          ).DeleteResourceDiscount(),
        )
      }
      if (valueType === 'surcharge') {
        updates.push(() =>
          this.getResourceAPI(
            data.workingStep.resource.id,
          ).DeleteResourceSurcharge(),
        )
      }
    }

    return updates
  }
}

export { PriceSummariesController }
